import { Box, Button, CircularProgress, Stack } from '@mui/material';
import { ActionButtons as ActionButtonsType, ActionButton } from 'app/concierge/types';
import { useState } from 'react';

interface ActionResult {
    success: boolean;
    message?: string;
}

interface IProps {
    actionButtons: ActionButtonsType;
    onAction?: (action: string, payload: any) => Promise<ActionResult> | ActionResult;
}

export default function ActionButtons({ actionButtons, onAction }: IProps) {
    const [loadingButtonId, setLoadingButtonId] = useState<string | null>(null);
    const [completedButtonIds, setCompletedButtonIds] = useState<string[]>([]);
    const [resultMessage, setResultMessage] = useState<string | null>(null);

    const handleClick = async (button: ActionButton) => {
        if (!onAction) {
            console.warn('ActionButtons: onAction callback is not provided');
            return;
        }

        setLoadingButtonId(button.id);
        setResultMessage(null);

        try {
            const result = await onAction(button.action, button.payload);
            if (result?.success) {
                setCompletedButtonIds((prev) => [...prev, button.id]);
                if (result.message) {
                    setResultMessage(result.message);
                }
            } else if (result) {
                if (result.message) {
                    setResultMessage(result.message);
                }
            }
        } catch (error) {
            console.error('ActionButtons: Error executing action', error);
            setResultMessage('An error occurred while executing the action');
        } finally {
            setLoadingButtonId(null);
        }
    };

    return (
        <Stack spacing={1} sx={{ mt: 2 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {actionButtons.buttons.map((button) => {
                    const isLoading = loadingButtonId === button.id;
                    const isCompleted = completedButtonIds.includes(button.id);
                    const isPrimary = button.variant !== 'secondary';

                    return (
                        <Button
                            key={button.id}
                            variant={isPrimary ? 'contained' : 'outlined'}
                            size="small"
                            disabled={isLoading || isCompleted || !onAction}
                            onClick={() => handleClick(button)}
                            sx={{
                                textTransform: 'none',
                                minWidth: 'auto',
                            }}
                        >
                            {isLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                            {button.label}
                        </Button>
                    );
                })}
            </Stack>
            {resultMessage && (
                <Box
                    sx={{
                        fontSize: '0.875rem',
                        color: 'text.secondary',
                        mt: 1,
                    }}
                >
                    {resultMessage}
                </Box>
            )}
        </Stack>
    );
}
